import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import qs from 'qs';
import firebase from 'firebase/app';
import { useLocation } from 'react-router-dom';
import {
  Alert, Button, Checkbox, Space, Spin, Typography,
} from 'antd';

import ClientGoogleLogin from '../../../components/client/ClientGoogleLogin';
import ClientEmailLogin from '../../../components/client/ClientEmailLogin';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useAccessibleClients } from '../../../models/useAccessibleClients';
import { payerApiOrigin, PAYER_ORIGIN_MIXED_CONTENT_MESSAGE } from '../../../utils/payerApiOrigin';
import { assignLocation } from '../../../utils/assignLocation';

import '../../../App.scss';

const { Title, Paragraph, Text } = Typography;

/**
 * OAuth 2.1 authorization endpoint for AI connections (Claude, ChatGPT, Claude Code → MCP).
 *
 * The person lands here from the AI client with standard PKCE query params, signs in with
 * Firebase, reviews WHO is asking, WHAT they get and WHICH accounts are included, then
 * approves or declines. Approve mints a code on strike-payer (`POST /oauth/authorization-code`,
 * Bearer Firebase ID token) and sends the browser back to `redirect_uri` with `code` + `state`.
 * Decline sends `error=access_denied`. Nothing is stored until strike-payer mints the code.
 *
 * Sign-in deliberately reuses the simple Google + email widgets the Stripe-app screen uses.
 * The Stripe-app screen (`/client/stripe-app-oauth`) stays separate: it is first-party and
 * auto-approves. See strikepay docs/MCP_OAUTH_PLAN.md §4.
 */

/** Plain words for each scope, in the order the server advertises them. */
export const SCOPE_DESCRIPTIONS = {
  'strike:self:read': 'See your own tips, balance, transactions and payouts',
  'strike:teams:read': 'See performance for the teams and accounts you select below',
  'strike:payroll:report': 'Prepare payroll and tax-period tip reports for the selected accounts',
  'strike:admin:client': 'Strikepay staff: report on any client (granted only to admins)',
  'offline_access': 'Stay connected without signing in again',
};

const ACCOUNT_SCOPES = ['strike:teams:read', 'strike:payroll:report'];
const ADMIN_SCOPE = 'strike:admin:client';
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

export function parseAuthorizeParams(search) {
  const q = qs.parse(search, { ignoreQueryPrefix: true }) || {};
  return {
    response_type: q.response_type,
    client_id: q.client_id,
    redirect_uri: q.redirect_uri,
    state: q.state,
    code_challenge: q.code_challenge,
    code_challenge_method: q.code_challenge_method,
    scope: q.scope,
    resource: q.resource,
  };
}

export function authorizeParamsValid(p) {
  return Boolean(
    p.response_type === 'code'
    && p.client_id
    && p.redirect_uri
    && p.state != null
    && p.code_challenge
    && p.code_challenge_method === 'S256'
    && p.resource,
  );
}

/** Space-separated scope → unique list; empty means "everything the server supports". */
export function parseScopeParam(raw) {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  return [...new Set(raw.split(/\s+/).filter((s) => s.length > 0))];
}

/** Host shown on the consent screen and whether it is a program on this computer. */
export function redirectHostInfo(redirectUri) {
  try {
    const u = new URL(redirectUri);
    return { host: u.host, isLoopback: u.protocol === 'http:' && LOOPBACK_HOSTS.includes(u.hostname) };
  } catch (e) {
    return { host: String(redirectUri || ''), isLoopback: false };
  }
}

/** `redirect_uri` plus the OAuth response params, existing query preserved. */
export function buildRedirect(redirectUri, params) {
  const target = new URL(redirectUri);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null) {
      target.searchParams.set(k, String(v));
    }
  });
  return target.toString();
}

/** @internal exported for unit tests only */
export const exportedForOAuthAuthorizeTests = {
  parseAuthorizeParams,
  authorizeParamsValid,
  parseScopeParam,
  redirectHostInfo,
  buildRedirect,
};

function ScopeList({ scopes }) {
  return (
    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
      {scopes.map((s) => (
        <li key={s}>
          {SCOPE_DESCRIPTIONS[s] || (
            <span>
              Unknown permission <code>{s}</code>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function AccountPicker({ clients, selected, onChange }) {
  if (clients === null) {
    return <Spin size="small" />;
  }
  if (clients.length === 0) {
    return (
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        You have no accounts or teams to include. Only your own data will be shared.
      </Paragraph>
    );
  }
  return (
    <Checkbox.Group
      value={selected}
      onChange={onChange}
      style={{ display: 'block' }}
    >
      <Space direction="vertical">
        {clients.map((c) => (
          <Checkbox key={c.id} value={c.id}>
            {c.displayName || c.id}
            {c.clientType === 'team' ? <Text type="secondary"> · team</Text> : null}
            {c.via === 'delegate' ? <Text type="secondary"> · shared with you</Text> : null}
          </Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
  );
}

export default function ClientOAuthAuthorizeScreen() {
  const location = useLocation();
  const [authLoading, user] = useCurrentUser({ signInAnonymously: false });
  const params = useMemo(() => parseAuthorizeParams(location.search || ''), [location.search]);
  const valid = useMemo(() => authorizeParamsValid(params), [params]);
  const scopes = useMemo(() => parseScopeParam(params.scope), [params.scope]);
  const wantsAccounts = scopes.length === 0 || scopes.some((s) => ACCOUNT_SCOPES.includes(s));
  // The admin connector asks for the admin scope. Strike admins sign in with Google only
  // (mirrors /admin/login), so that request gets the Google widget alone.
  const adminRequest = scopes.includes(ADMIN_SCOPE);
  const hostInfo = useMemo(() => redirectHostInfo(params.redirect_uri), [params.redirect_uri]);

  const signedIn = !authLoading && user && !user.isAnonymous;
  const clients = useAccessibleClients(signedIn && wantsAccounts ? user.uid : null);

  const [clientName, setClientName] = useState(null);
  const [selected, setSelected] = useState(null); // null = not yet defaulted
  const [phase, setPhase] = useState('idle'); // idle | submitting | done | error
  const [errorMessage, setErrorMessage] = useState(null);

  // Name the client from strike-payer's public lookup; fall back to the redirect host.
  useEffect(() => {
    if (!valid) {
      return undefined;
    }
    const base = payerApiOrigin();
    if (!base) {
      return undefined;
    }
    let cancelled = false;
    fetch(`${base}/v2/api/oauth/clients/${encodeURIComponent(params.client_id)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled && body && typeof body.client_name === 'string') {
          setClientName(body.client_name);
        }
      })
      .catch(() => { /* fall back to the host */ });
    return () => { cancelled = true; };
  }, [valid, params.client_id]);

  // Default to every accessible account once they load; the person can untick.
  useEffect(() => {
    if (selected === null && Array.isArray(clients)) {
      setSelected(clients.map((c) => c.id));
    }
  }, [clients, selected]);

  // Two Strikepay logins (personal and business) are common for owners and admins: the
  // consent must make it obvious WHICH account is being connected and let them change it.
  // Signing out flips useCurrentUser to null, so the sign-in card returns with the same
  // OAuth params still in the URL — the AI client's request is not lost.
  const switchAccount = useCallback(async () => {
    setPhase('idle');
    setErrorMessage(null);
    setSelected(null);
    try {
      await firebase.auth().signOut();
    } catch (e) {
      /* the auth listener decides what the screen shows next */
    }
  }, []);

  const deny = useCallback(() => {
    assignLocation(buildRedirect(params.redirect_uri, { error: 'access_denied', state: params.state }));
  }, [params.redirect_uri, params.state]);

  const approve = useCallback(async () => {
    setPhase('submitting');
    setErrorMessage(null);
    try {
      const base = payerApiOrigin();
      if (!base) {
        throw new Error(PAYER_ORIGIN_MIXED_CONTENT_MESSAGE);
      }
      const idToken = await user.getIdToken();
      const body = {
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        code_challenge: params.code_challenge,
        code_challenge_method: 'S256',
        resource: params.resource,
        strike_client_ids: wantsAccounts ? (selected || []) : [],
      };
      if (scopes.length > 0) {
        body.scope = scopes.join(' ');
      }
      const res = await fetch(`${base}/v2/api/oauth/authorization-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(out.error_description || out.error || res.statusText || 'Authorization failed');
      }
      if (!out.code || typeof out.code !== 'string') {
        throw new Error('Invalid response from strike-payer');
      }
      setPhase('done');
      assignLocation(buildRedirect(params.redirect_uri, { code: out.code, state: params.state }));
    } catch (e) {
      setPhase('error');
      setErrorMessage(e instanceof Error ? e.message : 'Authorization failed');
    }
  }, [user, params, scopes, selected, wantsAccounts]);

  if (!valid) {
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Title level={3}>Connect to Strikepay</Title>
        <Alert
          type="error"
          showIcon
          message="Invalid or incomplete OAuth request"
          description="Start the connection from your AI assistant (for example Claude → Connectors). The request must include response_type=code, client_id, redirect_uri, state, code_challenge, code_challenge_method=S256 and resource."
        />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 64 }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading…</Paragraph>
      </div>
    );
  }

  const who = clientName || hostInfo.host;

  if (!signedIn) {
    return (
      <div style={{ maxWidth: 480, margin: '32px auto', padding: 16 }}>
        <Title level={3}>Sign in to Strikepay</Title>
        <Paragraph type="secondary">
          <Text strong>{who}</Text>
          {' '}
          wants to connect to your Strikepay account.
          {' '}
          {adminRequest
            ? 'This is the Strikepay staff connection: sign in with your Strikepay Google account.'
            : 'Sign in with the account you use in the Strikepay dashboard; you will review what it can access before anything is shared.'}
        </Paragraph>
        <div style={{ background: '#000', borderRadius: 8, padding: '20px 0', marginTop: 8 }}>
          <div style={{ padding: '0 24px', marginBottom: 8 }}>
            <ClientGoogleLogin />
          </div>
          {adminRequest ? null : <ClientEmailLogin />}
        </div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div style={{ textAlign: 'center', marginTop: 64 }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>
          Sending you back to
          {' '}
          {hostInfo.host}
          …
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '32px auto', padding: 16 }} data-testid="oauth-consent">
      <Title level={3}>
        Connect
        {' '}
        {who}
        {' '}
        to Strikepay
      </Title>
      <Paragraph type="secondary">
        Connecting as
        {' '}
        <Text strong>{user.email || user.phoneNumber || user.uid}</Text>
        {' · '}
        <Button type="link" size="small" style={{ padding: 0 }} onClick={switchAccount} data-testid="oauth-switch-account">
          Not you? Switch account
        </Button>
      </Paragraph>

      {hostInfo.isLoopback ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This connection goes to a program on your computer"
          description={`After you approve, your browser will open ${hostInfo.host}. Only continue if you started this from an app you trust on this computer (for example Claude Code).`}
        />
      ) : (
        <Paragraph>
          After you approve, your browser will return to
          {' '}
          <Text code>{hostInfo.host}</Text>
          .
        </Paragraph>
      )}

      <Title level={5}>It will be able to</Title>
      <ScopeList scopes={scopes.length > 0 ? scopes : Object.keys(SCOPE_DESCRIPTIONS).filter((s) => s !== 'strike:admin:client')} />

      {wantsAccounts ? (
        <div style={{ marginTop: 16 }}>
          <Title level={5}>Accounts and teams to include</Title>
          <AccountPicker clients={clients} selected={selected || []} onChange={setSelected} />
        </div>
      ) : null}

      <Paragraph type="secondary" style={{ marginTop: 16 }}>
        This never gives the assistant more than you can already see in Strikepay. You can pause
        or disconnect it at any time from your Strikepay account.
      </Paragraph>

      {phase === 'error' ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Could not connect" description={errorMessage} />
      ) : null}

      <Space>
        <Button
          type="primary"
          size="large"
          loading={phase === 'submitting'}
          disabled={wantsAccounts && clients === null}
          onClick={approve}
          data-testid="oauth-approve"
        >
          Approve
        </Button>
        <Button size="large" onClick={deny} disabled={phase === 'submitting'} data-testid="oauth-deny">
          Don&apos;t allow
        </Button>
      </Space>
    </div>
  );
}
